import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getInventory, exportInventoryPdf } from '../lib/api';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { useNavigate } from 'react-router-dom';

const CARD_BG = { background: '#1C1C1E', border: '1px solid #2C2C2E' };
const CHIP_BG = { background: '#2C2C2E' };
const DIM     = { color: '#8E8E93' };
const DIMMER  = { color: '#636366' };

function todayIST() {
  const now = new Date();
  const ist = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
  return ist.toISOString().split('T')[0];
}

function daysAgo(n) {
  const now = new Date();
  const ist = new Date(now.getTime() + 5.5 * 60 * 60 * 1000 - n * 86400000);
  return ist.toISOString().split('T')[0];
}

function fmtDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d} ${months[parseInt(m, 10) - 1]} ${y}`;
}

function exportCSV(movements, dateFrom, dateTo) {
  const headers = ['Date','Time','Movement Type','Item','Quantity Change','Unit','Running Balance','Party','Submitted By'];
  const rows = movements.map(m => [
    m.date, m.time, m.movement_type, m.item_name,
    m.quantity_change >= 0 ? `+${m.quantity_change}` : m.quantity_change,
    m.unit, m.running_balance, m.party_name, m.submitted_by,
  ]);
  const csv = [headers, ...rows].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `inventory_${dateFrom}_to_${dateTo}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const DATE_PRESETS = [
  { label: '7d',  days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

const InventoryPage = () => {
  const { business } = useAuth();
  const navigate = useNavigate();

  const [datePreset, setDatePreset] = useState(30);
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [activeTab, setActiveTab]   = useState('stock');

  const dateFrom = daysAgo(datePreset);
  const dateTo   = todayIST();

  const load = useCallback(async () => {
    if (!business?.id) return;
    try {
      setLoading(true);
      setError(null);
      const res = await getInventory(business.id, { date_from: dateFrom, date_to: dateTo });
      setData(res.data);
    } catch (err) {
      setError('Could not load inventory. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [business, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const stockSummary  = data?.stock_summary || [];
  const movements     = data?.stock_movements || [];
  const lowAlerts     = data?.low_stock_alerts || [];

  const getStockStatus = (item) => {
    if (item.is_critical) return { color: '#FF453A', bg: 'rgba(255,69,58,.15)', label: 'Out of Stock', barColor: '#FF453A' };
    if (item.is_low)      return { color: '#FF9F0A', bg: 'rgba(255,159,10,.15)', label: 'Low Stock',   barColor: '#FF9F0A' };
    return                       { color: '#30D158', bg: 'rgba(48,209,88,.15)', label: 'In Stock',    barColor: '#30D158' };
  };

  return (
    <div className="min-h-screen text-white" data-testid="inventory-page"
      style={{ background: '#000', fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif" }}>

      {/* ── Header ── */}
      <header className="p-4 sticky top-0 z-10"
        style={{ background: 'rgba(0,0,0,.9)', backdropFilter: 'saturate(180%) blur(20px)', borderBottom: '1px solid #1C1C1E' }}>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">Inventory</h1>
            <p className="text-sm" style={DIM}>Stock Levels</p>
          </div>

          {/* Export buttons */}
          <div className="flex gap-1.5">
            <button
              onClick={() => movements.length && exportCSV(movements, dateFrom, dateTo)}
              disabled={!movements.length}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors hover:bg-white/10 disabled:opacity-30"
              style={{ border: '1px solid #2C2C2E', color: '#8E8E93' }}
              data-testid="export-csv-btn">CSV</button>
            <a href={business?.id ? exportInventoryPdf(business.id, dateFrom, dateTo) : '#'}
              target="_blank" rel="noreferrer"
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors hover:bg-white/10"
              style={{ border: '1px solid #2C2C2E', color: '#8E8E93' }}
              data-testid="export-pdf-btn">PDF</a>
          </div>

          <button onClick={load}
            className="p-2 rounded-full transition-colors hover:bg-white/10" data-testid="inventory-refresh-btn">
            <svg className="w-4 h-4" fill="none" stroke="#8E8E93" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {/* Date range presets */}
        <div className="flex gap-2 mt-3">
          {DATE_PRESETS.map(({ label, days }) => (
            <button key={days}
              onClick={() => setDatePreset(days)}
              className="px-3 py-1 rounded-full text-xs font-medium transition-all"
              style={{
                background: datePreset === days ? '#3A3A3C' : 'transparent',
                color: datePreset === days ? '#fff' : '#8E8E93',
                border: `1px solid ${datePreset === days ? '#636366' : '#2C2C2E'}`,
              }}
              data-testid={`date-preset-${label}`}>
              Last {label}
            </button>
          ))}
          <span className="ml-auto text-xs self-center" style={DIMMER}>
            {fmtDate(dateFrom)} — {fmtDate(dateTo)}
          </span>
        </div>
      </header>

      <ScrollArea className="h-[calc(100vh-120px)]">
        <main className="p-4 space-y-4">

          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl"
              style={{ background: 'rgba(255,69,58,.1)', border: '1px solid rgba(255,69,58,.2)' }}>
              <span className="text-xs" style={{ color: '#FF453A' }}>{error}</span>
            </div>
          )}

          {loading && (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2"
                style={{ borderColor: 'transparent transparent #FF2D55 transparent' }} />
            </div>
          )}

          {/* ── Low stock alerts ── */}
          {lowAlerts.length > 0 && (
            <div className="rounded-xl p-3 flex items-start gap-3"
              style={{ background: 'rgba(255,159,10,.08)', border: '1px solid rgba(255,159,10,.25)' }}
              data-testid="low-stock-alert">
              <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" stroke="#FF9F0A" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-medium" style={{ color: '#FF9F0A' }}>
                  {lowAlerts.length} item{lowAlerts.length !== 1 ? 's' : ''} need restocking
                </p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {lowAlerts.map(a => (
                    <span key={a.name} className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background: 'rgba(255,159,10,.15)', color: '#FF9F0A' }}>
                      {a.name}: {a.current_stock} {a.unit}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Tabs ── */}
          <div className="flex gap-1 p-1 rounded-xl" style={CHIP_BG}>
            {[
              { key: 'stock',     label: `Stock (${stockSummary.length})` },
              { key: 'movements', label: `Movements (${movements.length})` },
            ].map(({ key, label }) => (
              <button key={key}
                onClick={() => setActiveTab(key)}
                className="flex-1 py-1.5 rounded-lg text-sm font-medium transition-all"
                style={{
                  background: activeTab === key ? '#3A3A3C' : 'transparent',
                  color: activeTab === key ? '#fff' : '#8E8E93',
                }}
                data-testid={`tab-${key}`}>
                {label}
              </button>
            ))}
          </div>

          {/* ── Stock Summary Tab ── */}
          {activeTab === 'stock' && (
            <div className="space-y-3" data-testid="inventory-list">
              {stockSummary.length === 0 ? (
                <div className="rounded-2xl p-8 text-center" style={CARD_BG}>
                  <svg className="w-10 h-10 mx-auto mb-3" style={{ color: '#3A3A3C' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <p className="font-medium" style={DIM}>No stock entries yet</p>
                  <p className="text-sm mt-1" style={DIMMER}>Send a WhatsApp message to add stock</p>
                  <p className="text-xs mt-3 px-4 py-2 rounded-xl inline-block"
                    style={CHIP_BG}>
                    e.g. "100 bags cement aaya" or "50 rods purchase hua"
                  </p>
                </div>
              ) : (
                stockSummary.map((item) => {
                  const status = getStockStatus(item);
                  const pct    = item.max_stock > 0
                    ? Math.max(0, Math.min((item.current_stock / item.max_stock) * 100, 100))
                    : 0;
                  return (
                    <div key={item.name} className="rounded-2xl p-4" style={{
                      ...CARD_BG,
                      borderColor: item.is_critical ? 'rgba(255,69,58,.4)' : '#2C2C2E',
                    }} data-testid={`stock-item-${item.name}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-medium text-white">{item.name}</p>
                          <p className="text-sm mt-0.5" style={DIM}>{item.unit}</p>
                          {item.last_updated && (
                            <p className="text-xs mt-0.5" style={DIMMER}>Updated {item.last_updated}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold" style={{ color: status.color }}>
                            {Math.round(item.current_stock)}
                          </p>
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                            style={{ color: status.color, background: status.bg }}>
                            {status.label}
                          </span>
                        </div>
                      </div>
                      <div className="h-1.5 rounded-full overflow-hidden" style={CHIP_BG}>
                        <div className="h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, background: status.barColor }} />
                      </div>
                      <div className="flex justify-between text-xs mt-1" style={DIMMER}>
                        <span>Min: {item.min_stock}</span>
                        <span>Max: {item.max_stock}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* ── Movements Tab ── */}
          {activeTab === 'movements' && (
            <div data-testid="movements-section">
              {movements.length === 0 ? (
                <div className="rounded-2xl p-8 text-center" style={CARD_BG}>
                  <p className="font-medium" style={DIM}>No stock movements in this period</p>
                  <p className="text-sm mt-1" style={DIMMER}>Try a wider date range</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {movements.map((m) => {
                    const isIn  = m.quantity_change >= 0;
                    const color = isIn ? '#30D158' : '#FF453A';
                    return (
                      <div key={m.id} className="rounded-xl p-3"
                        style={{ ...CARD_BG, borderLeft: `3px solid ${color}` }}
                        data-testid="movement-row">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-white text-sm">{m.item_name}</p>
                              <span className="text-xs px-1.5 py-0.5 rounded"
                                style={{ background: '#2C2C2E', color: '#8E8E93' }}>
                                {m.movement_type}
                              </span>
                            </div>
                            <div className="flex items-center gap-3 mt-0.5">
                              <span className="text-xs" style={DIMMER}>{m.date} {m.time}</span>
                              {m.party_name && <span className="text-xs" style={DIM}>{m.party_name}</span>}
                              {m.submitted_by && <span className="text-xs" style={DIMMER}>by {m.submitted_by}</span>}
                            </div>
                          </div>
                          <div className="text-right ml-3 shrink-0">
                            <p className="font-bold text-sm" style={{ color }}>
                              {isIn ? '+' : ''}{Math.round(m.quantity_change)} {m.unit}
                            </p>
                            <p className="text-xs" style={DIMMER}>
                              Balance: <span style={{ color: '#8E8E93' }}>{Math.round(m.running_balance)}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

        </main>
      </ScrollArea>
    </div>
  );
};

export default InventoryPage;
